<template>
  <div class="login-page">
    <!-- 顶部导航栏 -->
    <div class="login-header">
      <div class="header-content">
        <div class="logo-section">
          <img class="brand-logo" :src="logoSrc" alt="北磁商标" @error="onLogoError" />
          <h1 class="system-title">工单与保修管理系统</h1>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="login-main">
      <div class="login-container">
        <div class="login-layout">
          <!-- 品牌信息面板 -->
          <el-card class="brand-card" shadow="never">
            <div class="brand-content">
              <h2 class="brand-title">工单与保修管理系统</h2>
              <p class="brand-subtitle">统一的工单流转、跟进、保修台账平台</p>
              <div class="brand-features">
                <div class="feature">
                  <el-icon class="feature-icon"><Tools /></el-icon>
                  <span>工单处理</span>
                </div>
                <div class="feature">
                  <el-icon class="feature-icon"><Search /></el-icon>
                  <span>流程跟进</span>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 登录表单卡片 -->
          <el-card
            class="login-card"
            shadow="hover"
          >
            <template #header>
              <div class="card-header">
                <el-icon class="login-icon" :size="24">
                  <User />
                </el-icon>
                <span class="login-title">管理员/处理人登录</span>
              </div>
            </template>
            
            <el-form
              ref="loginFormRef"
              :model="loginForm"
              :rules="rules"
              label-width="0"
              class="login-form"
              @submit.prevent="handleLogin"
            >
              <el-form-item prop="username" class="login-form-item">
                <el-input
                  v-model="loginForm.username"
                  class="login-input"
                  placeholder="请输入用户名"
                  size="large"
                  clearable
                >
                  <template #prefix>
                    <el-icon><User /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              
              <el-form-item prop="password" class="login-form-item">
                <el-input
                  v-model="loginForm.password"
                  class="login-input"
                  type="password"
                  placeholder="请输入密码"
                  size="large"
                  show-password
                  clearable
                  @keyup.enter="handleLogin"
                >
                  <template #prefix>
                    <el-icon><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              
              <el-form-item class="login-form-item">
                <el-button
                  type="primary"
                  size="large"
                  class="login-button"
                  :loading="loading"
                  @click="handleLogin"
                >
                  <el-icon v-if="!loading"><Right /></el-icon>
                  {{ loading ? '登录中...' : '登录' }}
                </el-button>
              </el-form-item>
            </el-form>
            
            
          </el-card>
        </div>
      </div>
    </div>

    <!-- 底部信息 -->
    <div class="login-footer">
      <div class="footer-inner">
        <el-text type="info" size="small">© 2026 北磁科技 - 版权所有</el-text>
        <el-text type="info" size="small">
          备案号：
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">冀ICP备2022027689号-1</a>
        </el-text>
      </div>
    </div>
    <div class="contact-float-panel">
      <div class="contact-tab">联系我们</div>
      <div class="contact-pop">
        <div class="contact-title">联系信息</div>
        <div class="contact-line">电话：<a href="tel:4000672725">4000672725</a></div>
        <div class="contact-line">邮箱：<a href="mailto:25350521@qq.com">25350521@qq.com</a></div>
        <div class="contact-actions">
          <el-button size="small" type="primary" @click="callPhone">拨打电话</el-button>
          <el-button size="small" @click="sendMail">发送邮件</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { Tools, User, Lock, Right, Search } from '@element-plus/icons-vue'
import { useLogin } from '@/composables/useLogin'
import logo from '@/assets/logo.png'

export default {
  name: 'LoginView',
  components: {
    Tools,
    User,
    Lock,
    Right,
    Search
  },
  setup() {
    // 使用分离的登录逻辑
    const {
      loginFormRef,
      loginForm,
      rules,
      loading,
      handleLogin
    } = useLogin()
    const logoSrc = ref('/png/商标标题.png')
    const onLogoError = () => { logoSrc.value = logo }
    const callPhone = () => { window.location.href = 'tel:4000672725' }
    const sendMail = () => { window.location.href = 'mailto:25350521@qq.com' }
    
    return {
      loginFormRef,
      loginForm,
      rules,
      loading,
      handleLogin,
      logoSrc,
      onLogoError,
      callPhone,
      sendMail
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  position: relative;
  background: linear-gradient(135deg, #1e6fd0 0%, #1a4f9e 45%, #0f2f5c 80%, #091f3d 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 背景装饰光斑 */
.login-page::before {
  content: '';
  position: absolute;
  top: -120px;
  right: -100px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(96,165,250,0.35) 0%, rgba(96,165,250,0) 70%);
  border-radius: 50%;
  pointer-events: none;
}

.login-page::after {
  content: '';
  position: absolute;
  bottom: -140px;
  left: -120px;
  width: 460px;
  height: 460px;
  background: radial-gradient(circle, rgba(59,130,246,0.25) 0%, rgba(59,130,246,0) 70%);
  border-radius: 50%;
  pointer-events: none;
}

/* 顶部导航栏 */
.login-header {
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 0 20px;
  height: 64px;
  color: #111827;
}

.header-content {
  height: 100%;
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-logo { height: 36px; width: auto; object-fit: contain; }

.system-title {
  margin: 0;
  color: #0f2f5c;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 1px;
}

/* 主要内容区域 */
.login-main {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.login-container {
  width: 100%;
  max-width: 980px;
  display: block;
}

.login-layout {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 24px;
  align-items: stretch;
}

/* 欢迎卡片 */
.brand-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e3a8a 100%);
  color: #ffffff;
  box-shadow: 0 12px 32px rgba(16, 52, 102, 0.35);
}

.brand-card::after {
  content: '';
  position: absolute;
  right: -50px;
  top: -50px;
  width: 180px;
  height: 180px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.brand-content {
  position: relative;
  z-index: 1;
  padding: 40px 32px;
}

.brand-title {
  margin: 0 0 10px 0;
  font-size: 1.9rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.brand-subtitle {
  margin: 0 0 24px 0;
  font-size: 1rem;
  opacity: 0.88;
}

.brand-features {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.feature {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 16px;
  border-radius: 10px;
  transition: background 0.25s;
}

.feature:hover {
  background: rgba(255, 255, 255, 0.26);
}

.feature-icon {
  color: #ffffff;
}

.feature span {
  color: #ffffff;
  font-weight: 500;
}

/* 登录卡片 */
.login-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  transition: box-shadow 0.3s, transform 0.3s;
}

.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.24);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0;
}

.login-icon {
  color: var(--el-color-primary);
}

.login-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #303133;
}

/* 登录表单 */
.login-form {
  padding: 20px 0;
}

.login-form-item {
  margin-bottom: 20px;
}

.login-button {
  width: 100%;
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  border: none;
  transition: all 0.3s;
}

.login-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
}

/* 测试账号提示已移除 */

/* 底部信息 */
.login-footer {
  position: relative;
  z-index: 2;
  background: #081a33;
  padding: 20px;
  text-align: center;
  color: #b6c2d6;
}
.login-footer .footer-inner {
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.login-footer a { color: #cfd3dc; text-decoration: none; border-bottom: 1px dashed rgba(255,255,255,0.35); }
.login-footer a:hover { color: #e5eaf5; border-bottom-color: #e5eaf5; }
.contact-float-panel { position: fixed; left: 16px; bottom: 16px; z-index: 50; color: #303133; }
.contact-tab { background: #ffffff; border: 1px solid #ebeef5; border-radius: 999px; box-shadow: 0 6px 16px rgba(17,24,39,0.18); padding: 10px 16px; font-weight: 600; cursor: pointer; transition: transform 0.2s; }
.contact-tab:hover { transform: translateY(-2px); }
.contact-line { font-size: 13px; color: #4b5563; }
.contact-pop { display: none; position: absolute; left: 0; bottom: 48px; background: #ffffff; border: 1px solid #ebeef5; border-radius: 14px; box-shadow: 0 12px 28px rgba(17,24,39,0.24); padding: 16px; width: 260px; gap: 8px; }
.contact-float-panel:hover .contact-pop { display: grid; }
.contact-title { font-weight: 600; color: #111827; }
.contact-actions { display: flex; gap: 8px; }

/* 响应式设计 */
@media (max-width: 768px) {
  .login-main {
    padding: 20px 15px;
  }
  
  .login-container {
    max-width: 100%;
  }

  .login-layout {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .system-title {
    font-size: 1.3rem;
  }
  
}

@media (max-width: 480px) {
  .login-header {
    padding: 0 15px;
  }
  
  .logo-section {
    gap: 8px;
  }
  
  .system-title {
    font-size: 1.2rem;
  }
  
  .login-form {
    padding: 15px 0;
  }
  
  .login-button {
    height: 44px;
  }
}

/* Element Plus 组件样式覆盖 */
.login-input {
  --el-input-border-radius: 6px;
  --el-input-hover-border-color: var(--el-color-primary);
  --el-input-focus-border-color: var(--el-color-primary);
}

</style>
